import { createPortal } from 'react-dom';
import React, { useRef, useState, useEffect } from 'react';

import { PADDING } from '@/core/constants';

type Props = {
  title: React.ReactNode;
  children: React.ReactNode;
};

export const Tooltip: React.FC<Props> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    const tooltipWidth = tooltipRef.current?.offsetWidth ?? 300;
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 0;

    const top = rect.top < tooltipHeight ? rect.bottom : rect.top - tooltipHeight;

    const left = Math.min(
      Math.max(PADDING, rect.left + rect.width / 2 - tooltipWidth / 2),
      window.innerWidth - tooltipWidth - PADDING
    );

    setCoords({ top, left });
  };

  const show = () => {
    if (open) return;

    setOpen(true);
    setVisible(true);
  };

  useEffect(() => {
    if (!open) return;

    calculatePosition();

    requestAnimationFrame(() => {
      calculatePosition();
    });
  }, [open]);

  const hide = () => {
    setVisible(false);
  };

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onTouchStart={() => (open ? hide() : show())}
    >
      {children}

      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 99999 }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <div
              onTransitionEnd={() => {
                if (!visible) {
                  setOpen(false);
                }
              }}
              className={`
                bg-white text-grey-800 text-xs rounded-xl
                shadow-xl border border-gray-200 px-3 py-2
                max-h-[200px] overflow-y-auto
                min-w-[200px] max-w-[300px]
                transition-all duration-150 ease-out
                ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}
              `}
            >
              {title}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
